import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { User, Mail, Phone, MapPin, Heart, Calendar, Briefcase, BookOpen, Save, CheckCircle, AlertCircle, Check, Lock, Eye, EyeOff, Shield, X } from 'lucide-react';

const DEFAULT_SECURITY_PIN = '123456';

const EditDetailsForm = ({ rollNumber, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  // Security PIN states
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [isDefaultPin, setIsDefaultPin] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);
  const [savingPin, setSavingPin] = useState(false);
  const [showPinSection, setShowPinSection] = useState(false); // Control PIN section visibility
  const [actualCurrentPin, setActualCurrentPin] = useState(''); // Store actual PIN from DB

  const [formData, setFormData] = useState({
    rollNumber: '',
    surname: '',
    firstName: '',
    fatherName: '',
    dob: '',
    gender: '',
    branch: '',
    currentAddress: '',
    permanentAddress: '',
    aadharCard: '',
    bloodGroup: '',
    abcId: '',
    hobbies: '',
    mobile: '',
    whatsapp: '',
    email: '',
    fatherMobile: '',
    fatherOccupation: '',
    motherName: '',
    motherMobile: '',
    motherOccupation: ''
  });

  useEffect(() => {
    loadStudentData();
    loadPinStatus();
  }, [rollNumber]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        setFormData({
          rollNumber: rollNumber || '',
          surname: studentData.surname || '',
          firstName: studentData.firstName || '',
          fatherName: studentData.fatherName || '',
          dob: studentData.dob || '',
          gender: studentData.gender || '',
          branch: studentData.branch || '',
          currentAddress: studentData.currentAddress || '',
          permanentAddress: studentData.permanentAddress || '',
          aadharCard: studentData.aadharCard || '',
          bloodGroup: studentData.bloodGroup || '',
          abcId: studentData.abcId || '',
          hobbies: studentData.hobbies || '',
          mobile: studentData.mobile || '',
          whatsapp: studentData.whatsapp || '',
          email: studentData.email || '',
          fatherMobile: studentData.fatherMobile || '',
          fatherOccupation: studentData.fatherOccupation || '',
          motherName: studentData.motherName || '',
          motherMobile: studentData.motherMobile || '',
          motherOccupation: studentData.motherOccupation || ''
        });
      }
    } catch (err) {
      console.error('Error loading student data:', err);
      setError('Failed to load student data');
    }
    setLoading(false);
  };

  // Load PIN status
  const loadPinStatus = async () => {
    try {
      const pinDoc = await getDoc(doc(db, 'secupin', rollNumber));
      if (!pinDoc.exists() || pinDoc.data().pin === DEFAULT_SECURITY_PIN) {
        setIsDefaultPin(true);
        setShowPinSection(true); // Always show if PIN is default
      } else {
        setIsDefaultPin(false);
        setShowPinSection(false); // Hide if PIN is already changed
      }
    } catch (error) {
      console.error('Error loading PIN status:', error);
    }
  };

  // Capitalize first letter of each word
  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Validate mobile number (10 digits only)
  const validateMobile = (value) => {
    return /^\d{0,10}$/.test(value);
  };

  // Validate ABC ID (12 digits only)
  const validateAbcId = (value) => {
    return /^\d{0,12}$/.test(value);
  };

  // Validate Aadhar (12 digits only)
  const validateAadhar = (value) => {
    return /^\d{0,12}$/.test(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Auto-capitalize names
    if (['firstName', 'surname', 'fatherName', 'motherName'].includes(name)) {
      newValue = capitalizeWords(value);
    }

    // Validate mobile numbers
    if (['mobile', 'whatsapp', 'fatherMobile', 'motherMobile'].includes(name)) {
      if (!validateMobile(value)) return;
      newValue = value;
    }

    // Validate ABC ID
    if (name === 'abcId') {
      if (!validateAbcId(value)) return;
      newValue = value;
    }

    // Validate Aadhar
    if (name === 'aadharCard') {
      if (!validateAadhar(value)) return;
      newValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    setSuccess(false);
    setError('');
  };

  // Handle checkbox for same address
  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);
    
    if (checked) {
      setFormData(prev => ({
        ...prev,
        currentAddress: prev.permanentAddress
      }));
    }
  };

  // Update current address when permanent address changes if checkbox is checked
  useEffect(() => {
    if (sameAsPermanent) {
      setFormData(prev => ({
        ...prev,
        currentAddress: prev.permanentAddress
      }));
    }
  }, [formData.permanentAddress, sameAsPermanent]);

  // Handle PIN Change
  const handlePinChange = async (e) => {
    e.preventDefault();
    setPinError('');
    setSavingPin(true);

    try {
      // Validate current PIN
      const pinDoc = await getDoc(doc(db, 'secupin', rollNumber));
      const storedPin = pinDoc.exists() ? pinDoc.data().pin : DEFAULT_SECURITY_PIN;

      if (storedPin !== currentPin) {
        setPinError('Current PIN is incorrect.');
        setSavingPin(false);
        return;
      }

      // Validate new PIN
      if (newPin.length !== 6) {
        setPinError('New PIN must be exactly 6 digits.');
        setSavingPin(false);
        return;
      }

      if (newPin === DEFAULT_SECURITY_PIN) {
        setPinError('Cannot use 123456 as your new PIN. Choose a different PIN for security.');
        setSavingPin(false);
        return;
      }

      if (newPin !== confirmNewPin) {
        setPinError('New PIN and confirmation do not match.');
        setSavingPin(false);
        return;
      }

      // Update PIN in Firestore
      await setDoc(doc(db, 'secupin', rollNumber), {
        rollNumber,
        pin: newPin,
        changedAt: new Date(),
        isDefault: false
      }, { merge: true });

      setPinSuccess(true);
      setIsDefaultPin(false);
      setCurrentPin('');
      setNewPin('');
      setConfirmNewPin('');
      
      setTimeout(() => {
        setPinSuccess(false);
        setShowPinSection(false); // Hide PIN section after successful change
      }, 2000);

    } catch (err) {
      console.error('Error changing PIN:', err);
      setPinError('An error occurred. Please try again.');
    } finally {
      setSavingPin(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - All fields required
    if (!formData.firstName || !formData.surname || !formData.fatherName || 
        !formData.dob || !formData.gender || !formData.branch ||
        !formData.currentAddress || !formData.permanentAddress || 
        !formData.aadharCard || !formData.bloodGroup || !formData.abcId ||
        !formData.hobbies || !formData.mobile || !formData.whatsapp || 
        !formData.email || !formData.fatherMobile || !formData.fatherOccupation ||
        !formData.motherName || !formData.motherMobile || !formData.motherOccupation) {
      setError('All fields are compulsory. Please fill in all information.');
      return;
    }

    // Validate mobile numbers are 10 digits
    if (formData.mobile.length !== 10) {
      setError('Mobile number must be exactly 10 digits');
      return;
    }

    if (formData.whatsapp.length !== 10) {
      setError('WhatsApp number must be exactly 10 digits');
      return;
    }

    if (formData.fatherMobile.length !== 10) {
      setError("Father's mobile number must be exactly 10 digits");
      return;
    }

    if (formData.motherMobile.length !== 10) {
      setError("Mother's mobile number must be exactly 10 digits");
      return;
    }

    // Validate ABC ID is 12 digits
    if (formData.abcId.length !== 12) {
      setError('ABC ID must be exactly 12 digits');
      return;
    }

    // Validate Aadhar is 12 digits
    if (formData.aadharCard.length !== 12) {
      setError('Aadhar Card number must be exactly 12 digits');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const studentRef = doc(db, 'students', rollNumber);
      
      const dataToSave = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(studentRef, dataToSave);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving data:', err);
      setError('Failed to save data. Please try again.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[90vh] overflow-y-auto">
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center space-x-3 animate-pulse">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-semibold">Information saved successfully!</span>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {/* ⭐ SECURITY PIN SECTION - AT THE TOP */}
      <div className="mb-8">
        {!showPinSection ? (
          // Collapsed view - Show button to change PIN
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Security PIN</h2>
                  <p className="text-green-700 font-medium">✓ Your PIN is secure</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPinSection(true)}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Lock className="w-5 h-5" />
                <span>Change PIN</span>
              </button>
            </div>
          </div>
        ) : (
          // Expanded view - Show PIN change form
          <div className={`${isDefaultPin ? 'bg-red-50 border-2 border-red-500' : 'bg-blue-50 border-2 border-blue-300'} rounded-xl p-6`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 ${isDefaultPin ? 'bg-red-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
                  <Shield className={`w-7 h-7 ${isDefaultPin ? 'text-red-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Security PIN</h2>
                  <p className="text-gray-600">Protect your account with a secure PIN</p>
                </div>
              </div>
              {!isDefaultPin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowPinSection(false);
                    setCurrentPin('');
                    setNewPin('');
                    setConfirmNewPin('');
                    setPinError('');
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* ⚠️ WARNING MESSAGE - Only shown when PIN is 123456 */}
            {isDefaultPin && (
              <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-600 rounded">
                <div className="flex items-start">
                  <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-red-800 font-bold text-lg mb-1">⚠️ Change your PIN immediately!</h3>
                    <p className="text-red-700 font-medium">
                      Your PIN is still set to the default <strong>123456</strong>. Your information is vulnerable! 
                      Please change it to something else right away.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {pinSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-green-800 font-medium">Security PIN changed successfully!</span>
              </div>
            )}

            {pinError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{pinError}</p>
              </div>
            )}

            <form onSubmit={handlePinChange} className="space-y-4">
              {/* Current PIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current PIN <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showCurrentPin ? "text" : "password"}
                    value={currentPin}
                    onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter your current PIN"
                    maxLength="6"
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xl tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPin(!showCurrentPin)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* New PIN */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New PIN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showNewPin ? "text" : "password"}
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter new PIN"
                      maxLength="6"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xl tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPin(!showNewPin)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm New PIN */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New PIN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showConfirmPin ? "text" : "password"}
                      value={confirmNewPin}
                      onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Confirm new PIN"
                      maxLength="6"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xl tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPin(!showConfirmPin)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={savingPin || !currentPin || !newPin || !confirmNewPin}
                className={`w-full md:w-auto px-6 py-3 ${isDefaultPin ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors`}
              >
                {savingPin ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Changing PIN...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    <span>Change Security PIN</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-600">
                <strong>Note:</strong> Your PIN must be 6 digits and cannot be 123456. Choose a memorable PIN that others cannot guess.
              </p>
            </form>
          </div>
        )}
      </div>

      {/* MAIN FORM - Student Information */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information Section */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-sm border border-indigo-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-indigo-600 rounded-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Roll Number
              </label>
              <input
                type="text"
                name="rollNumber"
                value={formData.rollNumber}
                disabled
                className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Surname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="Enter surname"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth <span className="text-red-500">*</span></label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Gender <span className="text-red-500">*</span></label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Blood Group <span className="text-red-500">*</span></label>
              <div className="relative">
                <Heart className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Select blood group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 shadow-sm border border-emerald-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-emerald-600 rounded-lg">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(10 digits)</span></label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                maxLength="10"
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="9876543210"
              />
              {formData.mobile && formData.mobile.length !== 10 && (
                <p className="text-xs text-amber-600 mt-1">Must be exactly 10 digits</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(10 digits)</span></label>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                maxLength="10"
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="9876543210"
              />
              {formData.whatsapp && formData.whatsapp.length !== 10 && (
                <p className="text-xs text-amber-600 mt-1">Must be exactly 10 digits</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                  placeholder="student@ybit.edu.in"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-6 shadow-sm border border-rose-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-rose-600 rounded-lg">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Address Information</h2>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Permanent Address <span className="text-red-500">*</span></label>
            <textarea
              name="permanentAddress"
              value={formData.permanentAddress}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all duration-200"
              placeholder="Enter permanent address"
            ></textarea>
          </div>

          <div className="flex items-center space-x-2 mb-3">
            <input
              type="checkbox"
              id="sameAddress"
              checked={sameAsPermanent}
              onChange={handleSameAddressChange}
              className="w-4 h-4 text-rose-600 bg-gray-100 border-gray-300 rounded focus:ring-rose-500"
            />
            <label htmlFor="sameAddress" className="text-sm font-medium text-gray-700 cursor-pointer">
              Current Address same as Permanent Address
            </label>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Current Address <span className="text-red-500">*</span></label>
            <textarea
              name="currentAddress"
              value={formData.currentAddress}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all duration-200"
              placeholder="Enter current address"
            ></textarea>
          </div>
        </div>

        {/* Documents & Academic Section */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 shadow-sm border border-amber-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-amber-600 rounded-lg">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Documents & Academic</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Aadhar Card <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(12 digits)</span></label>
              <input
                type="text"
                name="aadharCard"
                value={formData.aadharCard}
                onChange={handleChange}
                maxLength="12"
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                placeholder="123456789012"
              />
              {formData.aadharCard && formData.aadharCard.length !== 12 && (
                <p className="text-xs text-amber-600 mt-1">Must be exactly 12 digits</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">ABC ID <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(12 digits)</span></label>
              <input
                type="text"
                name="abcId"
                value={formData.abcId}
                onChange={handleChange}
                maxLength="12"
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                placeholder="123456789012"
              />
              {formData.abcId && formData.abcId.length !== 12 && (
                <p className="text-xs text-amber-600 mt-1">Must be exactly 12 digits</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Branch / Department <span className="text-red-500">*</span>
            </label>
            <select
              name="branch"
              value={formData.branch}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 appearance-none cursor-pointer"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                backgroundPosition: "right 0.5rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.5em 1.5em",
                paddingRight: "2.5rem"
              }}
            >
              <option value="">-- Select Branch --</option>
              <option value="Engineering Sciences">Engineering Sciences</option>
              <option value="Mechanical Engineering">Mechanical Engineering</option>
              <option value="Electrical Engineering">Electrical Engineering</option>
              <option value="Computer Engineering">Computer Engineering</option>
              <option value="Computer Science and Engineering">Computer Science and Engineering</option>
              <option value="AI&ML Engineering">AI&ML Engineering</option>
            </select>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Personal Hobbies / Interests <span className="text-red-500">*</span></label>
            <textarea
              name="hobbies"
              value={formData.hobbies}
              onChange={handleChange}
              rows="2"
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
              placeholder="e.g., Reading, Sports, Music, etc."
            ></textarea>
          </div>
        </div>

        {/* Parent Information Section */}
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-slate-600 rounded-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Parent Information</h2>
          </div>

          {/* Father's Information */}
          <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl p-5 mb-4 border-2 border-blue-200">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <User className="w-4 h-4 text-white" />
              </div>
              Father's Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="Father's name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(10 digits)</span>
                </label>
                <input
                  type="tel"
                  name="fatherMobile"
                  value={formData.fatherMobile}
                  onChange={handleChange}
                  maxLength="10"
                  className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="9876543210"
                />
                {formData.fatherMobile && formData.fatherMobile.length !== 10 && (
                  <p className="text-xs text-amber-600 mt-1">Must be exactly 10 digits</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Occupation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fatherOccupation"
                  value={formData.fatherOccupation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="e.g., Business, Service"
                />
              </div>
            </div>
          </div>

          {/* Mother's Information */}
          <div className="bg-gradient-to-br from-pink-100 to-pink-50 rounded-xl p-5 border-2 border-pink-200">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-pink-600 rounded-lg flex items-center justify-center mr-3">
                <User className="w-4 h-4 text-white" />
              </div>
              Mother's Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border-2 border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200"
                  placeholder="Mother's name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile <span className="text-red-500">*</span> <span className="text-xs text-gray-500">(10 digits)</span>
                </label>
                <input
                  type="tel"
                  name="motherMobile"
                  value={formData.motherMobile}
                  onChange={handleChange}
                  maxLength="10"
                  className="w-full px-4 py-3 bg-white border-2 border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200"
                  placeholder="9836378900"
                />
                {formData.motherMobile && formData.motherMobile.length !== 10 && (
                  <p className="text-xs text-amber-600 mt-1">Must be exactly 10 digits</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Occupation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="motherOccupation"
                  value={formData.motherOccupation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border-2 border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200"
                  placeholder="e.g., Homemaker, Service"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white pt-6 pb-2 border-t-2 border-gray-200 flex space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Update Information</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDetailsForm;
